import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, DUCK_LOGO, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, BadgeCheck } from "lucide-react";

const REF_STORAGE_KEY = "cq_ref";

export default function Register() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", confirm: "", referral_code: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [refInfo, setRefInfo] = useState(null); // {valid, label, owner_name}

  // 1) On mount, read ?ref= from URL or fall back to persisted code in localStorage.
  useEffect(() => {
    const raw = (params.get("ref") || localStorage.getItem(REF_STORAGE_KEY) || "").trim().toUpperCase();
    if (raw) {
      setForm((p) => ({ ...p, referral_code: raw }));
      localStorage.setItem(REF_STORAGE_KEY, raw);
      // Track the click + validate
      api.post("/referrals/track-click", { code: raw, path: "/register" }).catch(() => {});
      api.post("/referrals/validate", { code: raw }).then(({ data }) => {
        if (data?.valid) setRefInfo(data);
      }).catch(() => {});
    }
  // eslint-disable-next-line
  }, []);

  // Re-validate whenever the user manually edits the code
  useEffect(() => {
    const code = (form.referral_code || "").trim().toUpperCase();
    if (!code) { setRefInfo(null); return; }
    const t = setTimeout(() => {
      api.post("/referrals/validate", { code }).then(({ data }) => setRefInfo(data?.valid ? data : { valid: false })).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [form.referral_code]);

  if (user && user !== false) {
    const home = user.role === "client" ? "/portal" : user.role === "tradeline_admin" ? "/tradeline-admin" : "/admin";
    return <Navigate to={home} replace />;
  }

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, password: form.password,
        referral_code: (form.referral_code || "").trim().toUpperCase() || undefined,
      });
      if (data.token) localStorage.setItem("cq_token", data.token);
      localStorage.removeItem(REF_STORAGE_KEY);
      setUser(data);
      toast.success(`Welcome, ${data.first_name || form.first_name}!`);
      setTimeout(() => nav("/portal"), 300);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="relative hidden md:flex flex-col justify-between p-12 bg-gradient-to-br from-sky-50 via-white to-amber-50 overflow-hidden">
        <div className="cq-grain absolute inset-0" />
        <div className="relative flex items-center gap-3">
          <Link to="/" data-testid="register-brand-home" aria-label="Back to home"><img src={DUCK_LOGO} alt="" className="h-14 w-14 object-contain cq-waddle hover:scale-105 transition" /></Link>
          <div>
            <p className="font-display font-black text-2xl text-slate-900">Credit Quack</p>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Create your account</p>
          </div>
        </div>
        <div className="relative max-w-md">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-sky-600">Free to join</p>
          <h1 className="font-display text-5xl font-black leading-[1.05] text-slate-900 mt-4">Start tracking your credit repair — the transparent way.</h1>
          <p className="text-slate-600 text-lg mt-4">Sign up in 30 seconds and watch every step of your file in real time — no email verification required.</p>
          {refInfo?.valid && (
            <div className="mt-6 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 text-sm text-emerald-800 font-bold" data-testid="reg-ref-hero-badge">
              <BadgeCheck size={14} /> Sent by {refInfo.owner_name || refInfo.label} ({refInfo.code})
            </div>
          )}
        </div>
        <div className="relative text-sm text-slate-500">Already have an account? <Link to="/login" className="text-amber-600 font-bold">Sign in</Link></div>
      </div>

      <div className="flex items-center justify-center p-6 bg-white">
        <Card className="w-full max-w-md p-8 rounded-2xl border-slate-100 shadow-sm cq-in">
          <div className="mb-6 md:hidden flex items-center gap-3">
            <Link to="/" aria-label="Back to home"><img src={DUCK_LOGO} alt="" className="h-12 w-12 object-contain hover:scale-105 transition" /></Link>
            <p className="font-display font-black text-xl">Credit Quack</p>
          </div>
          <h2 className="font-display text-3xl font-black">Create account</h2>
          <p className="text-slate-500 mt-1 text-sm">Takes about 30 seconds.</p>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="reg-first-name" /></div>
              <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="reg-last-name" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="reg-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 0100" data-testid="reg-phone" /></div>
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="reg-password" /></div>
            <div><Label>Confirm password</Label><Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="reg-confirm" /></div>
            <div>
              <Label className="flex items-center gap-2">
                Referral / access code
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">optional</span>
              </Label>
              <Input
                value={form.referral_code}
                onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                placeholder="Enter your code"
                data-testid="reg-referral-code"
              />
              {form.referral_code && refInfo?.valid && (
                <p className="mt-1 text-xs text-emerald-700 font-bold inline-flex items-center gap-1" data-testid="reg-referral-valid">
                  <CheckCircle2 size={12} /> Referred by {refInfo.owner_name || refInfo.label}
                </p>
              )}
              {form.referral_code && refInfo && !refInfo.valid && (
                <p className="mt-1 text-xs text-rose-600 font-bold" data-testid="reg-referral-invalid">
                  Code not recognized — you can leave it blank.
                </p>
              )}
            </div>
            {err && <p className="text-sm text-red-600" data-testid="reg-error">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 h-11 rounded-xl" data-testid="reg-submit">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="mt-4 text-sm text-slate-500">Already registered? <Link to="/login" className="text-amber-600 font-bold">Sign in</Link></p>
        </Card>
      </div>
    </div>
  );
}
